import { useState, type FormEvent } from "react";
import { useApi } from "../lib/useApi";
import { ApiError } from "../lib/api";
import { isAcceptableDomain } from "../lib/domain";
import { Modal } from "./Modal";

/**
 * Sets — or clears — an organization's ChargeBee verified domain (`cf_verified_domain`), the field
 * that blocks anyone with a matching email domain from self-registering. The field is optional:
 * submitting it blank removes the verified domain.
 */
export function SetDomainDialog({ orgId, orgName, currentDomain, onClose, onDone }: {
  orgId: string;
  orgName: string;
  currentDomain: string | null;
  onClose: () => void;
  onDone?: () => void;
}) {
  const api = useApi();
  const [domain, setDomain] = useState(currentDomain ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const willClear = domain.trim() === "";
  const invalid = !isAcceptableDomain(domain);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (invalid) { setError("Enter a valid domain like company.com, or leave blank to remove."); return; }
    setBusy(true);
    setError(null);
    try {
      await api.setOrganizationVerifiedDomain(orgId, domain.trim());
      onDone?.();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : `${err}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={`Verified domain — ${orgName}`} onClose={onClose}>
      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 420 }}>
        <div>
          <div className="rail-label" style={{ marginBottom: 8 }}>Verified domain</div>
          <input
            type="text"
            placeholder="company.com"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            spellCheck={false}
            autoFocus
            style={{ width: "100%", fontSize: 12, padding: "8px 10px" }}
          />
          <div className="hint" style={{ marginTop: 5 }}>
            Blocks anyone with this email domain from self-registering a new organization. Leave blank to remove the verified domain.
          </div>
          {invalid && (
            <div className="hint" style={{ color: "var(--red)", marginTop: 5 }}>Enter a bare domain like <span className="mono">company.com</span> (no https://, path or spaces).</div>
          )}
        </div>

        {error && (
          <div className="callout error"><span className="mk">err</span><span>{error}</span></div>
        )}

        <div className="btn-row" style={{ justifyContent: "flex-end" }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button type="submit" className={"btn btn-primary btn-sm" + (busy ? " loading" : "")} disabled={busy || invalid}>
            {willClear ? "Remove domain" : "Set domain"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
